import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Download, Trash2, Star, Loader2, Eye, EyeOff } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * AI Photo Gallery — viewer for every AI-generated image of a saree.
 *
 * Lets the shop owner:
 *   - SEE every generated photo at full resolution (the dashboard tile only
 *     showed the cover; this is where they actually view the rest).
 *   - Pick a different cover (move any photo to index 0 = first thing customers see).
 *   - Delete a bad generation.
 *   - Download a photo for off-app use (WhatsApp posts, etc.).
 */
export default function AIGalleryDialog({ saree, open, onOpenChange, onChange }) {
    const [busy, setBusy] = useState(false);
    const images = saree?.ai_model_images || [];

    const persist = async (newImages) => {
        setBusy(true);
        try {
            await api.patch(`/admin/sarees/${saree.id}`, { ai_model_images: newImages });
            onChange?.(newImages);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
        } finally { setBusy(false); }
    };

    const setAsCover = async (idx) => {
        if (idx === 0) return;
        const next = [...images];
        const [pick] = next.splice(idx, 1);
        next.unshift(pick);
        toast.success("New cover set — this is what customers see first");
        await persist(next);
    };

    const removeOne = async (idx) => {
        if (!window.confirm("Delete this AI photo? Customers will no longer see it.")) return;
        const next = images.filter((_, i) => i !== idx);
        toast.success("Photo removed");
        await persist(next);
    };

    const download = (src, idx) => {
        const a = document.createElement("a");
        a.href = src;
        a.download = `${(saree?.title || "saree").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-ai-${idx + 1}.jpg`;
        a.click();
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-brand-paper border-brand-border max-w-3xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <div className="ayraa-overline flex items-center gap-1.5">
                        <Eye className="w-3 h-3" strokeWidth={1.5} />
                        AI Catalog Photos
                    </div>
                    <DialogTitle className="font-display text-2xl tracking-editorial mt-1">
                        {saree?.title || "Saree"}
                    </DialogTitle>
                </DialogHeader>

                <div className="flex items-center justify-between border-l-2 border-brand-bronze pl-3 py-2 bg-brand-subtle/60 rounded-sm my-4">
                    <div className="text-[12px] text-brand-ink leading-relaxed">
                        These are the only photos customers see. Original uploaded images
                        are <strong>never shown</strong> in the customer catalog.
                    </div>
                </div>

                {busy ? (
                    <div className="py-12 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-brand-bronze" /></div>
                ) : images.length === 0 ? (
                    <div className="py-16 text-center border border-dashed border-brand-border rounded-sm">
                        <EyeOff className="w-7 h-7 mx-auto text-brand-soft" strokeWidth={1.2} />
                        <div className="font-display text-xl mt-4 tracking-editorial">No AI photos yet</div>
                        <p className="text-brand-muted text-sm mt-2 max-w-sm mx-auto">
                            Run <span className="font-mono text-brand-ink">Magic Catalog</span> on this saree to generate
                            its first AI-styled photo. Until then it stays hidden from customers.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="ai-gallery-grid">
                        {images.map((src, idx) => (
                            <div key={idx} className="group relative">
                                <div className={`relative aspect-[3/4] rounded-sm overflow-hidden border-2 transition ${idx === 0 ? "border-brand-bronze" : "border-brand-border"}`}>
                                    <img src={src} alt={`ai-${idx + 1}`} className="w-full h-full object-cover" data-testid={`ai-gallery-img-${idx}`} />
                                    {idx === 0 ? (
                                        <div className="absolute top-2 left-2 font-mono text-[10px] uppercase tracking-wider-2 bg-brand-bronze text-white px-2 py-0.5 rounded-sm flex items-center gap-1">
                                            <Star className="w-2.5 h-2.5 fill-current" strokeWidth={2} /> Cover
                                        </div>
                                    ) : null}
                                </div>
                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-t from-black/75 via-transparent to-transparent flex items-end p-2.5 rounded-sm">
                                    <div className="flex gap-1.5">
                                        {idx !== 0 ? (
                                            <button
                                                type="button"
                                                onClick={() => setAsCover(idx)}
                                                className="bg-white/95 hover:bg-white text-brand-ink text-[10px] font-mono uppercase tracking-wider-2 px-2.5 py-1.5 rounded-sm transition"
                                                title="Make this the customer-facing cover photo"
                                                data-testid={`ai-gallery-cover-${idx}`}
                                            >
                                                <Star className="w-3 h-3 inline mr-1" strokeWidth={1.5} /> Cover
                                            </button>
                                        ) : null}
                                        <button
                                            type="button"
                                            onClick={() => download(src, idx)}
                                            className="bg-white/95 hover:bg-white text-brand-ink w-7 h-7 grid place-items-center rounded-sm transition"
                                            title="Download"
                                            data-testid={`ai-gallery-download-${idx}`}
                                        >
                                            <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => removeOne(idx)}
                                            className="bg-white/95 hover:bg-red-50 text-red-700 w-7 h-7 grid place-items-center rounded-sm transition"
                                            title="Delete"
                                            data-testid={`ai-gallery-delete-${idx}`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="text-xs text-brand-soft text-center mt-5 pt-4 border-t border-brand-border">
                    {images.length} AI {images.length === 1 ? "photo" : "photos"} total. Hover any photo to set cover, download, or delete.
                </div>
            </DialogContent>
        </Dialog>
    );
}
